import { useCallback, useEffect, useState } from "react";
import { useStore } from "../app/store.ts";
import { CopyButton } from "./CopyButton.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import {
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_TINY,
  Z_DEBUG,
} from "./tokens.ts";

interface FrameStats {
  fps: number;
  frameTime: number;
  cpuFrameTime: number;
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
  gpuRenderer?: string;
  jsHeapUsed?: number;
  jsHeapTotal?: number;
  jsHeapLimit?: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/**
 * Debug overlay panel — shown when debugEnabled is true in the store.
 * Renders at a fixed position and logs keyboard events, mode state, etc.
 * Extensible: add new debug sections as needed.
 */
export function DebugOverlay() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.debugOverlay);
  const mode = useStore((s) => s.mode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-overlay", () =>
    togglePanel("debugOverlay"),
  );

  const [log, setLog] = useState<string[]>([]);
  const [stats, setStats] = useState<FrameStats | null>(null);

  const addLine = useCallback((msg: string) => {
    setLog((prev) => [...prev.slice(-14), `${Date.now() % 100000}: ${msg}`]);
  }, []);

  // Poll frame stats from the viewport at ~4 Hz
  useEffect(() => {
    if (!debugEnabled) return;

    const id = setInterval(() => {
      const s = window.__poseref_frameStats;
      if (s) setStats({ ...s });
    }, 250);
    return () => clearInterval(id);
  }, [debugEnabled]);

  // Log all keydown events
  useEffect(() => {
    if (!debugEnabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      addLine(
        `↓ code=${e.code || "?"} key="${e.key}" kc=${e.keyCode} rep=${e.repeat}`,
      );
    };
    const onKeyUp = (e: KeyboardEvent) => {
      addLine(`↑ code=${e.code || "?"} key="${e.key}"`);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [debugEnabled, addLine]);

  const getPlainText = useCallback(() => {
    const lines: string[] = ["DEBUG", `mode: ${mode}`];
    if (stats) {
      lines.push("");
      lines.push(
        `${stats.fps.toFixed(1)} fps (${stats.frameTime.toFixed(1)} ms)`,
      );
      lines.push(
        `cpu: ${stats.cpuFrameTime.toFixed(2)} ms (${((stats.cpuFrameTime / stats.frameTime) * 100).toFixed(0)}% budget)`,
      );
      lines.push(
        `draw calls: ${stats.drawCalls} | tris: ${stats.triangles.toLocaleString()}`,
      );
      lines.push(`gpu: ${stats.gpuRenderer || "unknown"}`);
      lines.push(
        `geometries: ${stats.geometries} | textures: ${stats.textures}`,
      );
      if (stats.jsHeapUsed != null) {
        lines.push(
          `js heap: ${formatBytes(stats.jsHeapUsed)} / ${formatBytes(stats.jsHeapTotal!)} (limit ${formatBytes(stats.jsHeapLimit!)})`,
        );
      }
    }
    if (log.length > 0) {
      lines.push("", "Keyboard events:");
      lines.push(...log);
    }
    return lines.join("\n");
  }, [mode, stats, log]);

  if (!debugEnabled || !panelOpen) return null;

  const fpsColor =
    stats && stats.fps >= 55
      ? "#0f0"
      : stats && stats.fps >= 30
        ? "#ff0"
        : "#f44";

  const sectionBorder = {
    borderTop: "1px solid #333",
    marginTop: 4,
    paddingTop: 4,
  } as const;

  return (
    <DraggablePanel
      id="debug-overlay"
      defaultPosition={{ top: 8, right: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 220 }}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          <span style={{ color: "#ff0", fontWeight: "bold" }}>DEBUG</span>
          <CopyButton getText={getPlainText} />
        </div>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          color: "#0f0",
          fontFamily: "monospace",
          fontSize: FONT_SIZE_TINY,
          padding: "6px 10px",
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          maxWidth: 380,
          lineHeight: 1.5,
        }}
      >
        <div>
          mode: <b>{mode}</b>
        </div>

        {/* Frame stats */}
        <div style={sectionBorder}>
          {stats ? (
            <>
              <div>
                <span style={{ color: fpsColor }}>
                  {stats.fps.toFixed(1)} fps
                </span>{" "}
                <span style={{ color: COLOR_TEXT_DIM }}>
                  ({stats.frameTime.toFixed(1)} ms)
                </span>
              </div>
              <div style={{ color: COLOR_TEXT_DIM }}>
                cpu: {stats.cpuFrameTime.toFixed(2)} ms (
                {((stats.cpuFrameTime / stats.frameTime) * 100).toFixed(0)}%
                budget)
              </div>
              <div style={{ color: COLOR_TEXT_DIM }}>
                draw calls: {stats.drawCalls} | tris:{" "}
                {stats.triangles.toLocaleString()}
              </div>
            </>
          ) : (
            <span style={{ color: COLOR_TEXT_FAINT }}>
              waiting for frame data…
            </span>
          )}
        </div>

        {/* GPU info */}
        {stats && (
          <div style={sectionBorder}>
            <div style={{ color: COLOR_TEXT_DIM }}>
              gpu: {stats.gpuRenderer || "unknown"}
            </div>
            <div style={{ color: COLOR_TEXT_DIM }}>
              geometries: {stats.geometries} | textures: {stats.textures}
            </div>
          </div>
        )}

        {/* Memory */}
        {stats && stats.jsHeapUsed != null && (
          <div style={sectionBorder}>
            <div style={{ color: COLOR_TEXT_DIM }}>
              js heap: {formatBytes(stats.jsHeapUsed)} /{" "}
              {formatBytes(stats.jsHeapTotal!)} (limit{" "}
              {formatBytes(stats.jsHeapLimit!)})
            </div>
          </div>
        )}

        {/* Keyboard events */}
        <div
          style={{
            ...sectionBorder,
            color: COLOR_TEXT_DIM,
          }}
        >
          Keyboard events:
        </div>
        {log.length === 0 ? (
          <span style={{ color: COLOR_TEXT_FAINT }}>waiting for input…</span>
        ) : (
          log.map((line, i) => <div key={i}>{line}</div>)
        )}
      </div>
    </DraggablePanel>
  );
}
